import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdAutoFixHigh, MdRefresh, MdSearch, MdSend, MdExpandMore, MdCloseFullscreen } from 'react-icons/md';
import { useStylingAssistantContext } from '../../context/StylingAssistantContext';
import type { SATheme } from '../../types/stylingAssistant.types';
import PageHeader from '../common/PageHeader';

// Reusable sub-components for chat
import SASearchInput from './SASearchInput';
import SAChatMessage from './SAChatMessage';
import SALoadingIndicator from './SALoadingIndicator';

// ─── Dark theme ─────────────────────────────────────────────────────────────

const THEME: SATheme = {
  bg: '#0f0f0f',
  surface: '#141414',
  text: '#ffffff',
  textSecondary: '#9ca3af',
  textMuted: '#4b5563',
  border: 'rgba(255,255,255,0.08)',
  accent: '#EE4423',
  cardBg: '#1a1a1a',
  userMessageBg: '#2c2c2c',
  assistantMessageBg: '#1a1a1a',
};

// ─── Enhanced grid data for full page ───────────────────────────────────────

const SEARCH_TERMS = [
  'I am looking for elegant cocktail party dresses for a night event',
  'Looking for structured handbags in a modern aesthetic',
  'Looking for blue dresses with a refined silhouette',
  'Una chaqueta de moto inspirada en la motocicleta con un diseño estructurado y vanguardista',
  "I am looking for women's tops without buttons",
  'I am looking for winter outfits',
  "I am looking for men's tops between 50 and 150 dollars",
];

type ProductItem = { type: 'product'; image: string };
type SearchItem = { type: 'search'; searchIndex: number };
type GridItem = ProductItem | SearchItem;

interface GridRow {
  items: GridItem[];
  height: string;
}

const PAGE_GRID: GridRow[] = [
  {
    height: '220px',
    items: [
      { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/qh5Pu5hf4wA2jWMbw5XhjlMlTPD7dVJx3xlYlqPyzo.jpg?v=1747409696&width=900' },
      { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/f_TLe9ibr1iDga06rl8EzKyIOLKinpMm7H3I2UyIy38.jpg?v=1728944729&width=900' },
      { type: 'search', searchIndex: 0 },
      { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/t-BIJc-hXLeov96TETNMsgi6icgzCX5nOkpqMNy-qlg.jpg?v=1752247564&width=900' },
      { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/wk1PyFr5azG0tw73bmtuH-aJUa-6lI317ovf-Tz8CJ4.jpg?v=1752247711&width=900' },
    ],
  },
  {
    height: '220px',
    items: [
      { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/L1811-LCL419X_1N001_OS_A?w=1280' },
      { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/L0810-LCL120G_1G003_OS_A?w=1280' },
      { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/L1835-ISA073X_1N001_OS_A?w=1280' },
      { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/L1755-LCL095G_1N001_OS_A?w=1280' },
      { type: 'search', searchIndex: 1 },
    ],
  },
  {
    height: '220px',
    items: [
      { type: 'search', searchIndex: 2 },
      { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/HKoHNn6otgYUbe8pXtD_Dwaf3m3DOeYUuf2NTMgomVk.jpg?v=1752242447&width=900' },
      { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/fZbnaFJKCM8wtF9jMrySVbsBbfnjUhJr9GZSUe8nT6o.jpg?v=1746758057&width=900' },
      { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/6kKZ11ih3ryi5OOAfTMqIniQXTlJIhVLJLYAztM6wto.jpg?v=1747431630&width=900' },
      { type: 'product', image: 'https://maggylondon.com/cdn/shop/files/UB3wT7ZGvdWHOJsOGgfJJBY3TAQe7tRxIcjHj0XVtMw.jpg?v=1752076970&width=900' },
    ],
  },
  {
    height: '220px',
    items: [
      { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/fabulous-furs-ocean-fox-faux-fur-happy-hour-jacket__09303_grande.jpg?v=1727293846' },
      { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/fabulous-furs-olive-faux-fur-le-mink-jacket__75581_grande.jpg?v=1725470552' },
      { type: 'search', searchIndex: 5 },
      { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/zay1vpyl1pexucv8jw9p_grande.jpg?v=1724430120' },
      { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/cslcqnrycvyqqoikfjpg_grande.jpg?v=1728312050' },
    ],
  },
  {
    height: '220px',
    items: [
      { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/CSL764-LEX314_LB999_OS_A?w=1280' },
      { type: 'search', searchIndex: 3 },
      { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/LBS010-LMN014S24_LB999_OS_A?w=1280' },
      { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/LBS039-LMS002S23_HB425_OS_A?w=1280' },
      { type: 'product', image: 'https://cdn.media.amplience.net/i/tom_ford/LXR004-LMN014S24_LB999_OS_A?w=1280' },
    ],
  },
  {
    height: '220px',
    items: [
      { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/wlfcolszscgmlg9bcmqp_grande.jpg?v=1715963946' },
      { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/ripley-rader-tops-1-xs-navy-navy-ponte-knit-tank-top-39228615917788_grande.jpg?v=1719417494' },
      { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/x9cs2j7eie3xl0ejh3ur_grande.png?v=1718976943' },
      { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/a4d9rrpotsfru9cktjca_grande.jpg?v=1722360866' },
      { type: 'search', searchIndex: 4 },
    ],
  },
  {
    height: '220px',
    items: [
      { type: 'search', searchIndex: 6 },
      { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/lu9rgsx6f2xwayucwfb5_grande.jpg?v=1710257286' },
      { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/3a7dd10eb93511b766c567fba5c91997_grande.jpg?v=1695156423' },
      { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/oveka6or2lb69bggmjmr_grande.jpg?v=1715188300' },
      { type: 'product', image: 'https://www.sassanova.com/cdn/shop/files/yglt9ktkwhzbgka30hj8_grande.jpg?v=1724869504' },
    ],
  },
];

// ─── Component ──────────────────────────────────────────────────────────────

const StyleAssistantPage: React.FC = () => {
  const { sa, isExpandedFromPopup, collapseToPopup } = useStylingAssistantContext();
  const theme = THEME;

  useEffect(() => {
    if (sa.inputRef.current) {
      setTimeout(() => sa.inputRef.current?.focus(), 400);
    }
  }, []);

  return (
    <div className="h-[calc(100vh-20px)] flex flex-col">
      <PageHeader
        title="Style Assistant"
        showBackButton={false}
      />

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex-1 flex flex-col min-h-0 mt-2 rounded-2xl overflow-hidden"
        style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}
      >
        {/* ─── Chat Active Top Bar ────────────────────────────────── */}
        <AnimatePresence>
          {sa.messages.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="flex items-center justify-between px-6 py-2.5 flex-shrink-0"
              style={{ borderBottom: `1px solid ${theme.border}` }}
            >
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #EE4423 0%, #ff6b35 100%)' }}>
                  <MdAutoFixHigh size={14} className="text-white" />
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-xs font-medium" style={{ color: theme.textSecondary }}>AI Stylist Active</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Collapse back to popup (only if expanded from popup) */}
                {isExpandedFromPopup && (
                  <button
                    onClick={collapseToPopup}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 transition-all cursor-pointer group"
                    style={{ color: theme.textSecondary }}
                    title="Collapse back to popup"
                  >
                    <MdCloseFullscreen size={14} className="group-hover:scale-90 transition-transform duration-200" />
                    <span className="text-xs font-medium">Collapse</span>
                  </button>
                )}
                <button onClick={sa.handleRefresh} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 transition-all cursor-pointer group" style={{ color: theme.textSecondary }}>
                  <MdRefresh size={15} className="group-hover:rotate-180 transition-transform duration-500" />
                  <span className="text-xs font-medium">New chat</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ─── Scrollable Content ─────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto" style={{ backgroundColor: theme.bg }}>
          {sa.messages.length === 0 ? (
            /* ═══════════════════════════════════════════════════════
               ENHANCED WELCOME VIEW
               ═══════════════════════════════════════════════════════ */
            <div className="flex flex-col">
              {/* ─── Hero + Search ─────────────────────────────────── */}
              <div className="px-8 pt-7 pb-4">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.4 }}
                  className="flex items-center gap-3 mb-1"
                >
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #EE4423 0%, #ff6b35 100%)' }}>
                    <MdAutoFixHigh size={18} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-lg font-medium leading-tight" style={{ color: '#e6e6e8' }}>
                      Hello, what would you like to style today?
                    </h1>
                    <p className="text-[11px] mt-0.5" style={{ color: theme.textMuted }}>
                      Your personal AI stylist — describe any look, occasion, or mood
                    </p>
                  </div>
                </motion.div>

                {/* Search Bar */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                  className="mt-5 relative"
                >
                  <div
                    className="flex items-center gap-3 px-5 py-3.5 rounded-2xl transition-all duration-300 focus-within:border-[#EE4423]/30 focus-within:shadow-lg focus-within:shadow-[#EE4423]/5"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      border: '1.5px solid rgba(255, 255, 255, 0.08)',
                    }}
                  >
                    <MdSearch size={22} style={{ color: theme.textMuted }} className="flex-shrink-0" />
                    <input
                      ref={sa.inputRef}
                      type="text"
                      value={sa.inputValue}
                      onChange={(e) => sa.setInputValue(e.target.value)}
                      onKeyDown={sa.handleKeyPress}
                      placeholder="Ask me anything about style..."
                      className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-gray-500"
                      style={{ color: theme.text }}
                      disabled={sa.isLoading}
                    />
                    <button
                      onClick={() => sa.setShowSuggestionsDropdown(!sa.showSuggestionsDropdown)}
                      className="p-1.5 rounded-lg hover:bg-white/5 transition-all flex-shrink-0 cursor-pointer"
                      style={{ color: theme.textSecondary }}
                    >
                      <MdExpandMore
                        size={20}
                        className="transition-transform duration-200"
                        style={{ transform: sa.showSuggestionsDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      />
                    </button>
                    <button
                      onClick={sa.handleSend}
                      disabled={!sa.inputValue.trim() || sa.isLoading}
                      className="w-10 h-10 rounded-xl flex items-center justify-center transition-all hover:scale-105 disabled:opacity-20 disabled:cursor-not-allowed flex-shrink-0 cursor-pointer"
                      style={{
                        background: sa.inputValue.trim() ? 'linear-gradient(135deg, #EE4423 0%, #ff6b35 100%)' : 'rgba(255, 255, 255, 0.06)',
                      }}
                    >
                      <MdSend size={18} className="text-white" />
                    </button>
                  </div>

                  {/* Suggestions Dropdown */}
                  <AnimatePresence>
                    {sa.showSuggestionsDropdown && sa.searchItems.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: -4, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -4, scale: 0.98 }}
                        className="absolute left-0 right-0 top-full mt-2 rounded-2xl shadow-2xl z-50 max-h-72 overflow-y-auto"
                        style={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(238, 68, 35, 0.2)' }}
                      >
                        <div className="p-3">
                          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500 px-3 mb-2">Suggestions</p>
                          {sa.searchItems.map((suggestion, idx) => (
                            <button
                              key={idx}
                              onClick={() => { sa.handleSuggestionClick(suggestion); sa.setShowSuggestionsDropdown(false); }}
                              className="w-full text-left px-3 py-2.5 rounded-xl transition-all hover:bg-white/5 flex items-center gap-3 cursor-pointer group"
                            >
                              <MdSearch size={15} className="flex-shrink-0 text-gray-600 group-hover:text-[#EE4423] transition-colors" />
                              <span className="text-sm text-gray-300">{suggestion}</span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </div>

              {/* ─── Enhanced Product Grid ──────────────────────────── */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="px-6 pb-6"
              >
                <div className="space-y-4">
                  {PAGE_GRID.map((row, rowIndex) => (
                    <div
                      key={`row-${rowIndex}`}
                      className="grid gap-4"
                      style={{
                        gridTemplateColumns: row.items
                          .map((item) => item.type === 'search' ? 'minmax(0, 2fr)' : 'minmax(0, 1fr)')
                          .join(' '),
                      }}
                    >
                      {row.items.map((item, itemIndex) => {
                        if (item.type === 'search') {
                          const searchTerm = SEARCH_TERMS[(item as SearchItem).searchIndex];
                          return (
                            <motion.button
                              key={`s-${rowIndex}-${itemIndex}`}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.35 + rowIndex * 0.06 + itemIndex * 0.03, duration: 0.3 }}
                              onClick={() => sa.handleSuggestionClick(searchTerm)}
                              className="relative rounded-2xl cursor-pointer flex flex-col items-center justify-center text-center gap-3 group transition-all duration-300 hover:scale-[1.02]"
                              style={{
                                height: row.height,
                                backgroundColor: '#1e1e1e',
                                border: '1px solid rgba(255, 255, 255, 0.06)',
                              }}
                            >
                              {/* Hover glow */}
                              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: 'radial-gradient(circle at center, rgba(238, 68, 35, 0.06), transparent 70%)' }} />
                              <MdSearch size={28} className="text-gray-500 group-hover:text-[#EE4423] transition-colors duration-300 relative z-10" />
                              <p className="text-sm font-medium leading-snug px-4 max-w-[250px] relative z-10" style={{ color: '#d1d5db' }}>
                                {searchTerm}
                              </p>
                            </motion.button>
                          );
                        } else {
                          return (
                            <motion.div
                              key={`p-${rowIndex}-${itemIndex}`}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.35 + rowIndex * 0.06 + itemIndex * 0.03, duration: 0.3 }}
                              className="rounded-2xl overflow-hidden group cursor-pointer"
                              style={{ height: row.height }}
                            >
                              <div className="w-full h-full relative" style={{ backgroundColor: '#f5f5f5' }}>
                                <img
                                  src={(item as ProductItem).image}
                                  alt="Product"
                                  className="w-full h-full object-top object-cover transition-transform duration-500 group-hover:scale-110"
                                  loading="lazy"
                                />
                                {/* Hover overlay */}
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300" />
                              </div>
                            </motion.div>
                          );
                        }
                      })}
                    </div>
                  ))}
                </div>

                {/* Disclaimer */}
                <div className="text-center px-3 mt-5">
                  <p className="text-[10px] leading-tight" style={{ color: theme.textMuted }}>
                    By submitting a search via the virtual style assistant, you agree to the information being processed accordingly to our Terms & Conditions and Privacy Notice
                  </p>
                </div>
              </motion.div>
            </div>
          ) : (
            /* ═══════════════════════════════════════════════════════
               CHAT VIEW
               ═══════════════════════════════════════════════════════ */
            <div className="px-6 py-6 space-y-2 max-w-5xl mx-auto w-full">
              {sa.messages.map((message) => (
                <SAChatMessage
                  key={message.id}
                  message={message}
                  theme={theme}
                  activeMessageId={sa.activeMessageId}
                  visibleGroupIndexes={sa.visibleGroupIndexes}
                  isProgressivelyDisplaying={sa.isProgressivelyDisplaying}
                  groupRefs={sa.groupRefs}
                  carouselRefs={sa.carouselRefs}
                  onFollowUpClick={sa.handleFollowUpClick}
                  onScrollCarousel={sa.scrollCarousel}
                />
              ))}
              {sa.isLoading && <SALoadingIndicator theme={theme} />}
              <div ref={sa.messagesEndRef} />
            </div>
          )}
        </div>

        {/* ─── Bottom Input (Chat Mode) ───────────────────────────── */}
        {sa.messages.length > 0 && (
          <div className="px-6 py-4 flex-shrink-0" style={{ backgroundColor: theme.bg, borderTop: `1px solid ${theme.border}` }}>
            <div className="max-w-5xl mx-auto">
              <SASearchInput
                theme={theme}
                inputRef={sa.inputRef}
                inputValue={sa.inputValue}
                isLoading={sa.isLoading}
                showSuggestionsDropdown={sa.showSuggestionsDropdown}
                searchItems={sa.searchItems}
                dropdownDirection="up"
                onInputChange={sa.setInputValue}
                onKeyPress={sa.handleKeyPress}
                onSend={sa.handleSend}
                onToggleSuggestions={() => sa.setShowSuggestionsDropdown(!sa.showSuggestionsDropdown)}
                onSuggestionClick={(s) => { sa.handleSuggestionClick(s); sa.setShowSuggestionsDropdown(false); }}
              />
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default StyleAssistantPage;
